import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SalesLayout } from '@/components/SalesLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell, ComposedChart, Area } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette with more distinct colors
const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8AA', '#FF9F43'];

// Get current month date range (1st of month to current date)
const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  
  // Format as YYYY-MM-DD without timezone conversion
  const formatDate = (y, m, d) => {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };
  
  return {
    from: formatDate(year, month, 1),  // 1st of current month
    to: formatDate(year, month, day)    // Current date
  };
};

// Get days left in selected date range (0 if past month)
const getDaysLeftForDateRange = (dateFrom, dateTo) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const toDate = new Date(dateTo);
  toDate.setHours(23, 59, 59);
  
  // Calculate days from today until the end date
  const timeDiff = toDate.getTime() - today.getTime();
  const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  
  return Math.max(0, daysLeft);
};

function SalesRepPerformance({ user, onLogout }) {
  const navigate = useNavigate();
  const [bookings, setBookings] = React.useState([]);
  const [salesReps, setSalesReps] = React.useState([]);
  const [targets, setTargets] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const defaultRange = getCurrentMonthRange();
  const [dateFrom, setDateFrom] = React.useState(defaultRange.from);
  const [dateTo, setDateTo] = React.useState(defaultRange.to);
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedRep, setSelectedRep] = React.useState('');
  const [activeView, setActiveView] = React.useState('table');
  const [showTargetModal, setShowTargetModal] = React.useState(false);
  const [editingTarget, setEditingTarget] = React.useState({ rep: '', target: '', mobile: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [whatsappScheduled, setWhatsappScheduled] = React.useState(false);
  const [expandedRep, setExpandedRep] = React.useState(null);

  // Get selected month/year from dateFrom
  const selectedYear = React.useMemo(() => new Date(dateFrom).getFullYear(), [dateFrom]);
  const selectedMonth = React.useMemo(() => new Date(dateFrom).getMonth() + 1, [dateFrom]);

  React.useEffect(() => {
    fetchData();
  }, []);

  // Refetch targets when month changes
  React.useEffect(() => {
    fetchTargetsForMonth(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  const fetchTargetsForMonth = async (year, month) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sales-targets?year=${year}&month=${month}`);
      setTargets(response.data || {});
    } catch (error) {
      console.error('Fetch targets error:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [bookingsRes, mastersRes, targetsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings?limit=10000`),
        axios.get(`${BACKEND_URL}/api/masters?type=sales_representative`),
        axios.get(`${BACKEND_URL}/api/sales-targets?year=${selectedYear}&month=${selectedMonth}`)
      ]);
      
      setBookings(bookingsRes.data || []);
      setSalesReps(mastersRes.data || []);
      setTargets(targetsRes.data || {});
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  // Filter bookings by date range (DMS Invoice Date)
  const filteredBookings = React.useMemo(() => {
    return bookings.filter(b => {
      const invoiceDate = b.dms_invoice_date;
      if (!invoiceDate) return false;
      if (dateFrom && invoiceDate < dateFrom) return false;
      if (dateTo && invoiceDate > dateTo) return false;
      if (selectedRep && b.sales_representative !== selectedRep) return false;
      if (searchValue) {
        const search = searchValue.toLowerCase();
        return b.sales_representative?.toLowerCase().includes(search) ||
               b.customer_first_name?.toLowerCase().includes(search) ||
               b.booking_no?.toString().includes(search);
      }
      return true;
    });
  }, [bookings, dateFrom, dateTo, selectedRep, searchValue]);

  // Get previous year bookings for same period
  const getPreviousYearBookings = React.useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    
    const lyFrom = new Date(dateFrom);
    lyFrom.setFullYear(lyFrom.getFullYear() - 1);
    const lyTo = new Date(dateTo);
    lyTo.setFullYear(lyTo.getFullYear() - 1);
    
    const lyFromStr = lyFrom.toISOString().split('T')[0];
    const lyToStr = lyTo.toISOString().split('T')[0];
    
    return bookings.filter(b => {
      const invoiceDate = b.dms_invoice_date;
      if (!invoiceDate) return false;
      return invoiceDate >= lyFromStr && invoiceDate <= lyToStr;
    });
  }, [bookings, dateFrom, dateTo]);

  // Calculate days left based on selected date range
  const daysLeft = React.useMemo(() => {
    return getDaysLeftForDateRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  // Group data by Sales Rep - ONLY show reps with targets set
  const salesRepData = React.useMemo(() => {
    const cyData = {};
    const lyData = {};
    
    // Current Year data
    filteredBookings.forEach(b => {
      const rep = b.sales_representative || 'Unassigned';
      if (!cyData[rep]) {
        cyData[rep] = { count: 0, bookings: [] };
      }
      cyData[rep].count++;
      cyData[rep].bookings.push(b);
    });
    
    // Previous Year data
    getPreviousYearBookings.forEach(b => {
      const rep = b.sales_representative || 'Unassigned';
      if (!lyData[rep]) {
        lyData[rep] = { count: 0 };
      }
      lyData[rep].count++;
    });
    
    // ONLY include reps who have targets set for this month
    const repsWithTargets = Object.keys(targets).filter(rep => targets[rep]?.monthly_target > 0);
    
    // Build mobile lookup from Sales Rep Masters
    const masterMobileMap = {};
    salesReps.forEach(master => {
      // Try to get mobile from emp_mobile field (new field) or mobile field (legacy)
      const mobile = master.emp_mobile || master.mobile || '';
      if (master.name) {
        masterMobileMap[master.name] = mobile;
      }
    });
    
    return repsWithTargets.map(rep => {
      const cy = cyData[rep] || { count: 0, bookings: [] };
      const ly = lyData[rep] || { count: 0 };
      const target = targets[rep]?.monthly_target || 0;
      const deficit = Math.max(0, target - cy.count);
      const achievement = target > 0 ? ((cy.count / target) * 100).toFixed(1) : 0;
      const dailyRequired = daysLeft > 0 ? Math.ceil(deficit / daysLeft) : 0;
      
      // Get mobile from Sales Rep Masters (priority) or targets (fallback)
      const mobile = masterMobileMap[rep] || targets[rep]?.mobile || '';
      
      return {
        name: rep,
        mobile,
        cyCount: cy.count,
        lyCount: ly.count,
        growth: ly.count > 0 ? (((cy.count - ly.count) / ly.count) * 100).toFixed(1) : 'N/A',
        target,
        deficit,
        achievement: parseFloat(achievement),
        daysLeft,
        dailyRequired,
        bookings: cy.bookings
      };
    }).sort((a, b) => b.target - a.target || b.cyCount - a.cyCount);
  }, [filteredBookings, getPreviousYearBookings, targets, salesReps, daysLeft]);

  // Day-wise breakdown data for selected date range
  const dayWiseData = React.useMemo(() => {
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);
    const days = [];
    
    // Get all days in range
    for (let d = new Date(fromDate); d <= toDate; d.setDate(d.getDate() + 1)) {
      days.push(d.getDate());
    }
    
    // Get all unique sales reps
    const reps = [...new Set(filteredBookings.map(b => b.sales_representative).filter(Boolean))];
    
    // Create day-wise count for each rep
    const repDayData = {};
    reps.forEach(rep => {
      repDayData[rep] = { name: rep, days: {}, total: 0 };
      days.forEach(day => {
        repDayData[rep].days[day] = 0;
      });
    });
    
    // Count bookings per day per rep
    filteredBookings.forEach(b => {
      const rep = b.sales_representative;
      if (!rep || !repDayData[rep]) return;
      const date = new Date(b.dms_invoice_date);
      const day = date.getDate();
      if (repDayData[rep].days[day] !== undefined) {
        repDayData[rep].days[day]++;
        repDayData[rep].total++;
      }
    });
    
    return { days, reps: Object.values(repDayData) };
  }, [filteredBookings, dateFrom, dateTo]);

  // Summary metrics
  const summary = React.useMemo(() => {
    const totalCY = salesRepData.reduce((sum, r) => sum + r.cyCount, 0);
    const totalLY = salesRepData.reduce((sum, r) => sum + r.lyCount, 0);
    const totalTarget = salesRepData.reduce((sum, r) => sum + r.target, 0);
    const totalDeficit = salesRepData.reduce((sum, r) => sum + r.deficit, 0);
    const overallAchievement = totalTarget > 0 ? ((totalCY / totalTarget) * 100).toFixed(1) : 0;
    
    // Enhanced metrics
    const repsOnTrack = salesRepData.filter(r => r.achievement >= 100).length;
    const repsAtRisk = salesRepData.filter(r => r.achievement > 0 && r.achievement < 100).length;
    const repsZeroSales = salesRepData.filter(r => r.cyCount === 0).length;
    const avgDailyRequired = salesRepData.length > 0 
      ? (salesRepData.reduce((sum, r) => sum + r.dailyRequired, 0) / salesRepData.length).toFixed(1)
      : 0;
    
    return {
      totalCY,
      totalLY,
      totalTarget,
      totalDeficit,
      overallAchievement,
      growth: totalLY > 0 ? (((totalCY - totalLY) / totalLY) * 100).toFixed(1) : 'N/A',
      daysLeft,
      repsOnTrack,
      repsAtRisk,
      repsZeroSales,
      avgDailyRequired,
      totalReps: salesRepData.length
    };
  }, [salesRepData, daysLeft]);

  // Save target
  const handleSaveTarget = async () => {
    if (!editingTarget.rep) {
      toast.error('Please select a sales rep');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/sales-targets`, {
        sales_rep: editingTarget.rep,
        monthly_target: parseInt(editingTarget.target) || 0,
        mobile: editingTarget.mobile || '',
        year: editingTarget.year,
        month: editingTarget.month
      });
      
      toast.success(`Target saved for ${getMonthNameByNumber(editingTarget.month)} ${editingTarget.year}`);
      setShowTargetModal(false);
      setEditingTarget({ rep: '', target: '', mobile: '', year: selectedYear, month: selectedMonth });
      fetchTargetsForMonth(selectedYear, selectedMonth);
    } catch (error) {
      toast.error('Failed to save target');
    }
  };

  // Helper to get month name by number
  const getMonthNameByNumber = (monthNum) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthNum - 1] || '';
  };

  // Schedule WhatsApp
  const handleScheduleWhatsApp = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/sales-targets/schedule-whatsapp`, {
        enabled: !whatsappScheduled,
        time: '09:00'
      });
      
      setWhatsappScheduled(!whatsappScheduled);
      toast.success(whatsappScheduled ? 'WhatsApp automation disabled' : 'WhatsApp scheduled for 9:00 AM daily');
    } catch (error) {
      toast.error('Failed to schedule WhatsApp');
    }
  };

  // Send WhatsApp now
  const handleSendWhatsAppNow = async (rep) => {
    const message = encodeURIComponent(
      `🎯 *Sales Performance Update*\n\n` +
      `Dear ${rep.name},\n\n` +
      `📊 *Your Performance Summary:*\n` +
      `✅ Target: ${rep.target} units\n` +
      `📈 Achieved: ${rep.cyCount} units\n` +
      `📉 Deficit: ${rep.deficit} units\n` +
      `🏆 Achievement: ${rep.achievement}%\n` +
      `⏰ Days Left: ${rep.daysLeft}\n` +
      `📌 Daily Required: ${rep.dailyRequired} units/day\n\n` +
      `Keep pushing! 💪\n` +
      `- AutoBook Pro`
    );
    
    if (rep.mobile) {
      const phone = rep.mobile.replace(/\D/g, '');
      { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=91${phone}&text=${message}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
    } else {
      toast.error('Mobile number not available for ' + rep.name);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel...');
      const response = await axios.post(`${BACKEND_URL}/api/reports/sales-rep-export`, {
        data: salesRepData,
        dateFrom,
        dateTo
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SalesRep_Performance_${dateFrom}_to_${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Export Booking Details + Sales Performance to Excel (Combined)
  const handleExportBookingDetails = async () => {
    try {
      toast.info('Generating Excel with Booking Details & Sales Performance...');
      const response = await axios.post(`${BACKEND_URL}/api/reports/booking-details-export`, {
        bookings: filteredBookings.map(b => ({
          booking_no: b.booking_no,
          customer_name: `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim(),
          chassis_no: b.chassis_no,
          registration_number: b.registration_number,
          lead_source: b.lead_source,
          date_of_booking: b.date_of_booking,
          dms_invoice_date: b.dms_invoice_date,
          customer_balance: b.customer_balance,
          pme_drive_no: b.pme_drive_no,
          sales_representative: b.sales_representative
        })),
        salesPerformance: salesRepData.map(rep => ({
          name: rep.name,
          mobile: rep.mobile,
          target: rep.target,
          cyCount: rep.cyCount,
          deficit: rep.deficit,
          achievement: rep.achievement,
          daysLeft: rep.daysLeft,
          dailyRequired: rep.dailyRequired,
          lyCount: rep.lyCount,
          growth: rep.growth
        })),
        dateFrom,
        dateTo
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SalesRep_Report_${dateFrom}_to_${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded with Booking Details & Sales Performance');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Custom label for pie chart
  const renderCustomLabel = ({ name, percent }) => {
    return `${name} (${(percent * 100).toFixed(0)}%)`;
  };

  // Get month name from date
  const getMonthName = () => {
    const date = new Date(dateFrom);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Sales Rep Performance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Sales Rep Performance">
      <div className="space-y-4">
        {/* Date Range Selector - Gradient Pale Green */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div 
            className="flex items-center gap-4 p-4 rounded-xl shadow-lg"
            style={{ background: 'linear-gradient(135deg, #98D8AA 0%, #7BC89C 100%)' }}
          >
            <div className="text-white">
              <label className="block text-xs font-medium opacity-90 mb-1">From Date (DMS Invoice)</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 bg-white/90 border-0 rounded-lg text-gray-800 text-sm focus:ring-2 focus:ring-white/50"
              />
            </div>
            <div className="text-white">
              <label className="block text-xs font-medium opacity-90 mb-1">To Date (DMS Invoice)</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 bg-white/90 border-0 rounded-lg text-gray-800 text-sm focus:ring-2 focus:ring-white/50"
              />
            </div>
            <div className="text-white text-sm">
              <span className="opacity-75">Days Left:</span>
              <span className="font-bold ml-2 text-lg">{daysLeft}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowTargetModal(true)}
              className="px-4 py-2 bg-[#98D8AA] text-white rounded-lg hover:bg-[#7BC89C] text-sm font-medium shadow-md"
              data-testid="set-targets-btn"
            >
              🎯 Set Targets
            </button>
            <button
              onClick={handleScheduleWhatsApp}
              className={`px-4 py-2 rounded-lg text-sm font-medium shadow-md ${
                whatsappScheduled 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white border border-[#98D8AA] text-[#7BC89C]'
              }`}
              data-testid="schedule-whatsapp-btn"
            >
              📱 {whatsappScheduled ? 'WhatsApp Active' : 'Schedule WhatsApp'}
            </button>
            <button
              onClick={handleExportExcel}
              className="px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-xl hover:bg-indigo-50 text-sm shadow-sm transition-all duration-180 ease-out"
              data-testid="export-excel-btn"
            >
              📊 Export Excel
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-xl hover:bg-indigo-50 text-sm shadow-sm transition-all duration-180 ease-out"
              data-testid="print-btn"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Summary Metrics Cards — premium gradient KPI tiles (Royal Twilight) */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 anim-grid-stagger">
          <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(79,70,229,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.totalCY}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">CY Sales (Actual)</p>
          </div>
          <div className="bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.totalLY}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">LY Sales</p>
          </div>
          <div className={`bg-gradient-to-br rounded-2xl p-4 text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden ${
            parseFloat(summary.growth) >= 0 ? 'from-emerald-500 via-emerald-600 to-teal-700 shadow-[0_10px_30px_-8px_rgba(16,185,129,0.40)]' : 'from-rose-500 via-rose-600 to-red-700 shadow-[0_10px_30px_-8px_rgba(244,63,94,0.40)]'
          }`}>
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.growth === 'N/A' ? '-' : `${summary.growth}%`}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">Growth %</p>
          </div>
          <div className="bg-gradient-to-br from-violet-500 via-violet-600 to-purple-700 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(124,58,237,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.totalTarget}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">Total Target</p>
          </div>
          <div className="bg-gradient-to-br from-fuchsia-500 via-pink-600 to-rose-600 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(217,70,239,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.totalDeficit}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">Total Deficit</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-fuchsia-700 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(168,85,247,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.overallAchievement}%</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">Achievement</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(234,88,12,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{daysLeft}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">Days Left</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500 via-teal-600 to-emerald-700 rounded-2xl p-4 shadow-[0_10px_30px_-8px_rgba(20,184,166,0.40)] text-white hover:-translate-y-0.5 transition-all duration-240 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10 pointer-events-none"></div>
            <p className="text-3xl font-bold relative z-10" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{summary.totalReps}</p>
            <p className="text-[11px] uppercase tracking-[0.12em] opacity-85 font-semibold relative z-10 mt-1">Active Reps</p>
          </div>
        </div>

        {/* Performance Summary Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 anim-grid-stagger">
          <div className="bg-white/80 border-l-4 border-green-500 rounded-lg p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{summary.repsOnTrack}</p>
                <p className="text-xs text-gray-500">On Track (100%+)</p>
              </div>
              <span className="text-2xl">🏆</span>
            </div>
          </div>
          <div className="bg-white/80 border-l-4 border-yellow-500 rounded-lg p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-yellow-600">{summary.repsAtRisk}</p>
                <p className="text-xs text-gray-500">At Risk (&lt;100%)</p>
              </div>
              <span className="text-2xl">⚠️</span>
            </div>
          </div>
          <div className="bg-white/80 border-l-4 border-red-500 rounded-lg p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{summary.repsZeroSales}</p>
                <p className="text-xs text-gray-500">Zero Sales</p>
              </div>
              <span className="text-2xl">🚨</span>
            </div>
          </div>
          <div className="bg-white/80 border-l-4 border-blue-500 rounded-lg p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{summary.avgDailyRequired}</p>
                <p className="text-xs text-gray-500">Avg Daily Req.</p>
              </div>
              <span className="text-2xl">📈</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-xl border border-indigo-100/60 rounded-2xl p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search by name, booking..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-indigo-100/60 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400/60 focus:outline-none transition-all duration-240"
                data-testid="search-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sales Rep</label>
              <select
                value={selectedRep}
                onChange={(e) => setSelectedRep(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-indigo-100/60 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400/60 focus:outline-none transition-all duration-240"
                data-testid="sales-rep-filter"
              >
                <option value="">All Sales Reps</option>
                {[...new Set(bookings.map(b => b.sales_representative).filter(Boolean))].sort().map(rep => (
                  <option key={rep} value={rep}>{rep}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <button
                onClick={() => { setSearchValue(''); setSelectedRep(''); setDateFrom(defaultRange.from); setDateTo(defaultRange.to); }}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm"
                data-testid="reset-filters-btn"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2">
          {['table', 'daywise', 'charts', 'comparison', 'details'].map(view => (
            <button
              key={view}
              onClick={() => setActiveView(view)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === view
                  ? 'text-white shadow-md'
                  : 'bg-white/50 text-gray-600 hover:text-indigo-600'
              }`}
              style={activeView === view ? { background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' } : {}}
              data-testid={`view-${view}-btn`}
            >
              {view === 'table' && '📋 Table View'}
              {view === 'daywise' && '📅 Day-wise'}
              {view === 'charts' && '📊 Charts View'}
              {view === 'comparison' && '📈 CY vs LY'}
              {view === 'details' && '📝 Booking Details'}
            </button>
          ))}
        </div>

        {/* Table View */}
        {activeView === 'table' && (
          <div className="bg-white/80 backdrop-blur-xl border border-indigo-100/60 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-4 border-b border-indigo-100/60 bg-gradient-to-r from-indigo-50/40 via-violet-50/30 to-white">
              <h3 className="font-semibold text-slate-800 tracking-tight">Sales Rep Performance - Target Analysis</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="performance-table">
                <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                  <tr>
                    <th className="p-3 text-left font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">#</th>
                    <th className="p-3 text-left font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">Sales Rep</th>
                    <th className="p-3 text-left font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">Mobile</th>
                    <th className="p-3 text-center font-semibold bg-white/15 border-r border-white/20 tracking-wide text-[12px] uppercase">Target</th>
                    <th className="p-3 text-center font-semibold bg-white/15 border-r border-white/20 tracking-wide text-[12px] uppercase">Actual (CY)</th>
                    <th className="p-3 text-center font-semibold bg-rose-700/40 border-r border-white/20 tracking-wide text-[12px] uppercase">Deficit</th>
                    <th className="p-3 text-center font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">Achievement %</th>
                    <th className="p-3 text-center font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">Days Left</th>
                    <th className="p-3 text-center font-semibold bg-amber-600/40 border-r border-white/20 tracking-wide text-[12px] uppercase">Daily Req.</th>
                    <th className="p-3 text-center font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">LY Sales</th>
                    <th className="p-3 text-center font-semibold border-r border-white/20 tracking-wide text-[12px] uppercase">Growth %</th>
                    <th className="p-3 text-center font-semibold tracking-wide text-[12px] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salesRepData.length > 0 ? salesRepData.map((rep, idx) => (
                    <tr key={rep.name} className={`border-b border-indigo-50 transition-all duration-180 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30'}`}>
                      <td className="p-3 text-gray-500 border-r border-indigo-50">{idx + 1}</td>
                      <td className="p-3 font-medium text-slate-800 border-r border-indigo-50">{rep.name}</td>
                      <td className="p-3 text-gray-600 border-r border-indigo-50">{rep.mobile || '-'}</td>
                      <td className="p-3 text-center font-semibold text-indigo-700 bg-indigo-50/50 border-r border-indigo-50">{rep.target}</td>
                      <td className="p-3 text-center font-bold text-slate-800 bg-violet-50/40 border-r border-indigo-50">{rep.cyCount}</td>
                      <td className={`p-3 text-center font-semibold bg-rose-50/60 border-r border-indigo-50 ${rep.deficit > 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                        {rep.deficit > 0 ? `-${rep.deficit}` : '✓'}
                      </td>
                      <td className="p-3 text-center border-r border-indigo-50">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-600"
                              style={{ 
                                width: `${Math.min(100, rep.achievement)}%`,
                                background: rep.achievement >= 100 ? '#98D8AA' : rep.achievement >= 50 ? '#F4ACB7' : '#EF4444'
                              }}
                            ></div>
                          </div>
                          <span className={`font-medium ${rep.achievement >= 100 ? 'text-green-600' : 'text-gray-600'}`}>
                            {rep.achievement}%
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-center text-gray-600 border-r border-gray-100">{rep.daysLeft}</td>
                      <td className={`p-3 text-center font-semibold bg-yellow-50 border-r border-gray-100 ${rep.dailyRequired > 2 ? 'text-red-500' : 'text-gray-700'}`}>
                        {rep.dailyRequired}
                      </td>
                      <td className="p-3 text-center text-gray-500 border-r border-gray-100">{rep.lyCount}</td>
                      <td className={`p-3 text-center font-medium border-r border-gray-100 ${
                        rep.growth === 'N/A' ? 'text-gray-400' :
                        parseFloat(rep.growth) >= 0 ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {rep.growth !== 'N/A' ? `${rep.growth}%` : '-'}
                      </td>
                      <td className="p-3 text-center border-b border-gray-200 flex gap-1 justify-center">
                        <button
                          onClick={() => {
                            setEditingTarget({ 
                              rep: rep.name, 
                              target: rep.target.toString(), 
                              mobile: rep.mobile || '',
                              year: selectedYear,
                              month: selectedMonth
                            });
                            setShowTargetModal(true);
                          }}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                          title="Edit Target"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleSendWhatsAppNow(rep)}
                          className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200"
                          title="Send WhatsApp"
                        >
                          📱
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={12} className="p-8 text-center text-gray-400">No data found</td>
                    </tr>
                  )}
                </tbody>
                {salesRepData.length > 0 && (
                  <tfoot className="bg-[#98D8AA]/10 font-semibold">
                    <tr>
                      <td className="p-3" colSpan={3}>TOTAL</td>
                      <td className="p-3 text-center">{summary.totalTarget}</td>
                      <td className="p-3 text-center">{summary.totalCY}</td>
                      <td className="p-3 text-center text-red-500">{summary.totalDeficit}</td>
                      <td className="p-3 text-center">{summary.overallAchievement}%</td>
                      <td className="p-3 text-center">{summary.daysLeft}</td>
                      <td className="p-3 text-center">-</td>
                      <td className="p-3 text-center">{summary.totalLY}</td>
                      <td className="p-3 text-center">{summary.growth}%</td>
                      <td className="p-3"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Day-wise View */}
        {activeView === 'daywise' && (
          <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl overflow-hidden shadow-sm">
            <div className="p-4 border-b border-[#FFCAD4]/20 bg-[#FFE5D9]/20">
              <h3 className="font-semibold text-gray-800">Day-wise Achievement - {getMonthName()}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="daywise-table">
                <thead className="bg-[#98D8AA]/20 border-b-2 border-[#98D8AA]/40">
                  <tr>
                    <th className="p-2 text-left text-gray-700 font-semibold sticky left-0 bg-[#98D8AA]/20 min-w-[120px] border-r border-gray-200">Sales Rep</th>
                    {dayWiseData.days.map(day => (
                      <th key={day} className="p-2 text-center text-gray-700 font-semibold min-w-[40px] border-r border-gray-100">{day}</th>
                    ))}
                    <th className="p-2 text-center text-gray-700 font-semibold bg-[#98D8AA]/30 min-w-[60px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dayWiseData.reps.map((rep, idx) => (
                    <tr key={rep.name} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#FFF9F5]'} border-b border-gray-200`}>
                      <td className="p-2 font-medium text-gray-800 sticky left-0 bg-inherit border-r border-gray-200">{rep.name}</td>
                      {dayWiseData.days.map(day => (
                        <td key={day} className={`p-2 text-center border-r border-gray-100 ${rep.days[day] > 0 ? 'text-green-600 font-semibold' : 'text-gray-300'}`}>
                          {rep.days[day] || '-'}
                        </td>
                      ))}
                      <td className="p-2 text-center font-bold text-gray-800 bg-[#98D8AA]/10">{rep.total}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[#98D8AA]/20 font-semibold">
                  <tr>
                    <td className="p-2 sticky left-0 bg-[#98D8AA]/20">Total</td>
                    {dayWiseData.days.map(day => {
                      const dayTotal = dayWiseData.reps.reduce((sum, rep) => sum + (rep.days[day] || 0), 0);
                      return (
                        <td key={day} className="p-2 text-center">{dayTotal || '-'}</td>
                      );
                    })}
                    <td className="p-2 text-center bg-[#98D8AA]/30">{summary.totalCY}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Charts View */}
        {activeView === 'charts' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Target vs Actual Bar Chart */}
            <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Target vs Actual by Sales Rep</h3>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={salesRepData.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#FFCAD4" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-20} textAnchor="end" height={70} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #FFCAD4', borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="target" name="Target" fill="#98D8AA" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cyCount" name="Actual" fill="#F4ACB7" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="achievement" name="Achievement %" stroke="#E89AAB" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Sales Distribution Pie Chart - Improved */}
            <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Sales Distribution</h3>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={salesRepData.filter(r => r.cyCount > 0).slice(0, 8)}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name.substring(0, 15)} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    innerRadius={40}
                    dataKey="cyCount"
                    paddingAngle={2}
                  >
                    {salesRepData.filter(r => r.cyCount > 0).slice(0, 8).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} sales`, name]}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #FFCAD4', borderRadius: '8px' }}
                  />
                  <Legend layout="vertical" align="right" verticalAlign="middle" />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Deficit Analysis */}
            <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-6 shadow-sm md:col-span-2">
              <h3 className="font-semibold text-gray-800 mb-4">Deficit Analysis - Daily Requirement</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesRepData.filter(r => r.deficit > 0).slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#FFCAD4" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="name" type="category" width={120} stroke="#9ca3af" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #FFCAD4', borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="deficit" name="Deficit" fill="#EF4444" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="dailyRequired" name="Daily Required" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* CY vs LY Comparison View */}
        {activeView === 'comparison' && (
          <div className="grid grid-cols-1 gap-6">
            {/* Rep-wise CY vs LY */}
            <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Sales Rep - CY vs LY Comparison</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={salesRepData.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#FFCAD4" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-20} textAnchor="end" height={80} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #FFCAD4', borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="cyCount" name="CY Sales" fill="#98D8AA" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lyCount" name="LY Sales" fill="#FFCAD4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Booking Details View */}
        {activeView === 'details' && (
          <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl overflow-hidden shadow-sm">
            <div className="p-4 border-b border-[#FFCAD4]/20 bg-[#FFE5D9]/20 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-gray-800">Booking Details - {getMonthName()}</h3>
                <p className="text-xs text-gray-500 mt-1">Total: {filteredBookings.length} bookings</p>
              </div>
              <button
                onClick={handleExportBookingDetails}
                className="px-4 py-2 bg-[#98D8AA] text-white rounded-lg hover:bg-[#7BC89C] text-sm font-medium shadow-md flex items-center gap-2"
                data-testid="export-booking-details-btn"
              >
                📊 Export to Excel
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="booking-details-table">
                <thead className="bg-[#98D8AA]/20">
                  <tr>
                    <th className="p-3 text-left text-gray-700 font-semibold">Booking No</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Customer Name</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Chassis No</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Registration No</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Lead Source</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Booking Date</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">DMS Invoice Date</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Customer Balance</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">PME Drive No</th>
                    <th className="p-3 text-left text-gray-700 font-semibold">Sales Rep</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length > 0 ? filteredBookings.slice(0, 100).map((b, idx) => (
                    <tr key={b.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FFF9F5]'}>
                      <td className="p-3 font-medium text-blue-600">{b.booking_no}</td>
                      <td className="p-3">{`${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim() || '-'}</td>
                      <td className="p-3 font-mono text-xs">{b.chassis_no || '-'}</td>
                      <td className="p-3">{b.registration_number || '-'}</td>
                      <td className="p-3">{b.lead_source || '-'}</td>
                      <td className="p-3">{b.date_of_booking || '-'}</td>
                      <td className="p-3">{b.dms_invoice_date || '-'}</td>
                      <td className="p-3">{b.customer_balance || '-'}</td>
                      <td className="p-3">{b.pme_drive_no || '-'}</td>
                      <td className="p-3 text-sm">{b.sales_representative || '-'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-gray-400">No bookings found</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {filteredBookings.length > 100 && (
                <div className="p-4 text-center text-gray-500 text-sm bg-[#FFF9F5]">
                  Showing first 100 of {filteredBookings.length} bookings. Export to Excel for full data.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Target Setting Modal */}
      {showTargetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold text-gray-800 mb-4">🎯 Set Monthly Target</h3>
            
            <div className="space-y-4">
              {/* Month and Year Selection */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={editingTarget.month}
                    onChange={(e) => setEditingTarget({ ...editingTarget, month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-[#FFCAD4] rounded-lg"
                    data-testid="target-month-select"
                  >
                    {['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                      <option key={month} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={editingTarget.year}
                    onChange={(e) => setEditingTarget({ ...editingTarget, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-[#FFCAD4] rounded-lg"
                    data-testid="target-year-select"
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Representative</label>
                <select
                  value={editingTarget.rep}
                  onChange={(e) => setEditingTarget({ ...editingTarget, rep: e.target.value })}
                  className="w-full px-3 py-2 border border-[#FFCAD4] rounded-lg"
                  data-testid="target-sales-rep-select"
                >
                  <option value="">Select Sales Rep</option>
                  {[...new Set(bookings.map(b => b.sales_representative).filter(Boolean))].sort().map(rep => (
                    <option key={rep} value={rep}>{rep}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Target (Units)</label>
                <input
                  type="number"
                  value={editingTarget.target}
                  onChange={(e) => setEditingTarget({ ...editingTarget, target: e.target.value })}
                  className="w-full px-3 py-2 border border-[#FFCAD4] rounded-lg"
                  placeholder="e.g., 10"
                  data-testid="target-units-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={editingTarget.mobile}
                  onChange={(e) => setEditingTarget({ ...editingTarget, mobile: e.target.value })}
                  className="w-full px-3 py-2 border border-[#FFCAD4] rounded-lg"
                  placeholder="e.g., 9876543210"
                  data-testid="target-mobile-input"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowTargetModal(false); setEditingTarget({ rep: '', target: '', mobile: '', year: selectedYear, month: selectedMonth }); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                data-testid="cancel-target-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTarget}
                className="flex-1 px-4 py-2 text-white rounded-lg"
                style={{ background: 'linear-gradient(135deg, #98D8AA 0%, #7BC89C 100%)' }}
                data-testid="save-target-btn"
              >
                Save Target
              </button>
            </div>
          </div>
        </div>
      )}
    </SalesLayout>
  );
}

export default SalesRepPerformance;
